import { useHydrateAtoms } from "jotai/utils";
import {
  Account,
  BuiltInApp,
  Session,
  ZSL,
  accountAtom,
  canvasAtom,
  chatbotsAppAtom,
  currentAccountIdAtom,
  edgesAtom,
  interfacesAppAtom,
  isEmbedAtom,
  isPublicAtom,
  nodesAtom,
  sessionAtom,
  showCanvasTemplateWidgetAtom,
  tablesAppAtom,
  zslAtom,
  Toggles,
  togglesAtom,
  isViewOnlyDowntimeOverrideAtom,
  RawCanvas,
  isTemplatePageAtom,
  isViewOnlyModeAtom,
  composerServiceAtom,
} from "@zapier/canvas-state";
import { aiPromptAtom, showWelcomeModalAtom } from "@zapier/streaming";
import { useCurrentAccountId } from "@zapier/identity";
import { convertCanvasSchemaForReactFlow } from "../../utils/convertCanvasSchemaForReactFlow";
import { ComposerContextType, useComposer } from "@zapier/agent-system";

export function CanvasHydrator({
  canvas: rawCanvas,
  children,
  session,
  account,
  template,
  tablesApp,
  interfacesApp,
  chatbotsApp,
  toggles,
  isPublic = false,
  isEmbed = false,
  isAiPage = false,
  prompt = "",
  isTemplatePage = false,
}: {
  canvas: RawCanvas;
  children: React.ReactNode;
  session?: Session;
  account?: Account;
  template?: ZSL;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  chatbotsApp: BuiltInApp;
  toggles: Toggles;
  isPublic?: boolean;
  isEmbed?: boolean;
  isAiPage?: boolean;
  isTemplatePage?: boolean;
  prompt?: string;
  composerService?: ComposerContextType;
}) {
  const isViewer = rawCanvas.access_level === "can_view"; // This will be true when is_public is true (so true for public links)
  const composerService = useComposer();

  const isViewOnlyMode =
    isViewer ||
    isPublic ||
    isTemplatePage ||
    isEmbed ||
    toggles.viewOnlyDowntime;

  const canvas = convertCanvasSchemaForReactFlow({
    isViewOnlyMode,
    rawCanvas,
  });

  const showWelcomeModal = determineShowWelcomeModal({
    isAiPage,
    isViewOnlyMode,
    rawCanvas,
  });

  const currentAccountId = useCurrentAccountId();

  useHydrateAtoms([
    [isTemplatePageAtom, isTemplatePage],
    [zslAtom, template],
    [canvasAtom, canvas],
    [nodesAtom, canvas.nodes],
    [edgesAtom, canvas.edges],
    [currentAccountIdAtom, currentAccountId],
    [accountAtom, account || null],
    [sessionAtom, session || null],
    [tablesAppAtom, tablesApp],
    [interfacesAppAtom, interfacesApp],
    [chatbotsAppAtom, chatbotsApp],
    [togglesAtom, toggles],
    [isPublicAtom, isPublic],
    [isEmbedAtom, isEmbed],
    [showCanvasTemplateWidgetAtom, false],
    [showWelcomeModalAtom, showWelcomeModal],
    [aiPromptAtom, prompt],
    [isViewOnlyDowntimeOverrideAtom, toggles.viewOnlyDowntime],
    [isViewOnlyModeAtom, isViewOnlyMode],
    [composerServiceAtom, composerService],
  ]);

  return children;
}

function determineShowWelcomeModal({
  isAiPage,
  isViewOnlyMode,
  rawCanvas,
}: {
  isAiPage: boolean;
  isViewOnlyMode: boolean;
  rawCanvas: RawCanvas;
}) {
  const { nodes } = rawCanvas;
  if (isAiPage) {
    return false;
  }
  if (isViewOnlyMode) {
    return false;
  }

  return (
    nodes.length < 2 &&
    nodes.every((node) => node.node_type === "default") &&
    nodes.every((node) => node.label === "") &&
    nodes.every((node) => !node.services || node.services.length === 0)
  );
}
